import React, { useEffect, useRef } from "react";
import { useAgentMessage } from "rapida-react";

const avatar = "../assets/AIAssistantImage.jpeg"

interface MessageData {
  messages: string[];
}

interface MessageBubbleProps {
  messages: string[];
  isBot: boolean;
}

// Separate component for message bubbles to improve organization
const MessageBubble: React.FC<MessageBubbleProps> = ({ messages, isBot }) => {
  const timestamp = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  
  // Styles for the message bubbles
  const styles = {
    container: {
      display: "flex",
      flexDirection: "column" as const,
      alignItems: isBot ? "flex-start" : "flex-end",
      maxWidth: "85%",
      alignSelf: isBot ? "flex-start" : "flex-end",
      marginBottom: "1rem"
    },
    bubbleContainer: {
      display: "flex",
      position: "relative" as const,
      maxWidth: "100%"
    },
    avatar: {
      width: "32px",
      height: "32px",
      borderRadius: "50%",
      backgroundColor: isBot ? "#333333" : "#e95f43",
      display: "flex",
      alignItems: "center" as const,
      justifyContent: "center" as const,
      color: "#ffffff",
      fontSize: "14px",
      fontWeight: 600,
      marginRight: isBot ? "8px" : "0",
      marginLeft: isBot ? "0" : "8px",
      boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
      flexShrink: 0,
      order: isBot ? 0 : 1
    },
    bubbleWrapper: {
      display: "flex",
      flexDirection: "column" as const,
      maxWidth: "calc(100% - 40px)"
    },
    bubble: {
      backgroundColor: isBot ? "#2a2a2a" : "#e95f43",
      color: isBot ? "#f0f0f0" : "#ffffff",
      borderRadius: isBot 
        ? "18px 18px 18px 4px" 
        : "18px 18px 4px 18px",
      padding: "12px 16px",
      boxShadow: "0 2px 4px rgba(0, 0, 0, 0.15)",
      position: "relative" as const,
      overflow: "hidden" as const,
      wordBreak: "break-word" as const
    },
    text: {
      margin: "0 0 8px 0",
      lineHeight: "1.5",
      fontSize: "15px"
    },
    lastText: {
      margin: "0",
      lineHeight: "1.5",
      fontSize: "15px"
    },
    timestamp: {
      fontSize: "11px",
      color: isBot ? "rgba(240, 240, 240, 0.6)" : "rgba(255, 255, 255, 0.8)",
      marginTop: "4px",
      alignSelf: "flex-end" as const
    },
    metaInfo: {
      display: "flex",
      justifyContent: "space-between" as const,
      alignItems: "center" as const,
      width: "100%",
      paddingTop: "4px"
    },
    status: {
      display: "flex",
      alignItems: "center" as const,
      gap: "4px"
    },
    statusDot: {
      width: "6px",
      height: "6px",
      backgroundColor: "#4ade80",
      borderRadius: "50%"
    },
    statusText: {
      fontSize: "11px",
      color: isBot ? "rgba(240, 240, 240, 0.6)" : "rgba(255, 255, 255, 0.8)"
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.bubbleContainer}>
        <div style={styles.avatar}>{isBot ? "L" : "U"}</div>
        <div style={styles.bubbleWrapper}>
          <div style={styles.bubble}>
            {messages.map((message, index) => (
              <p 
                key={index} 
                style={index === messages.length - 1 ? styles.lastText : styles.text}
              >
                {message}
              </p>
            ))}
          </div>
          <div style={styles.metaInfo}>
            {!isBot && (
              <div style={styles.status}>
                <div style={styles.statusDot}></div>
                <span style={styles.statusText}>Sent</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Header component for the chat
const ChatHeader: React.FC = () => {
  const styles = {
    header: {
      display: "flex",
      alignItems: "center" as const,
      justifyContent: "space-between" as const,
      padding: "16px",
      borderBottom: "1px solid rgba(75, 85, 99, 0.2)",
      backgroundColor: "rgba(26, 26, 26, 0.95)",
      borderTopLeftRadius: "0.75rem",
      borderTopRightRadius: "0.75rem",
      backdropFilter: "blur(8px)",
      boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)"
    },
    headerLeft: {
      display: "flex",
      alignItems: "center" as const,
      gap: "12px"
    },
    avatar: {
      width: "40px",
      height: "40px",
      borderRadius: "50%",
      backgroundColor: "#e95f43",
      display: "flex",
      alignItems: "center" as const,
      justifyContent: "center" as const,
      color: "#ffffff",
      fontSize: "16px",
      fontWeight: 600,
      boxShadow: "0 2px 8px rgba(233, 95, 67, 0.3)"
    },
    headerInfo: {
      display: "flex",
      flexDirection: "column" as const
    },
    name: {
      fontWeight: 600,
      fontSize: "16px",
      color: "#f0f0f0"
    },
    status: {
      fontSize: "12px",
      color: "#4ade80",
      display: "flex",
      alignItems: "center" as const,
      gap: "4px"
    },
    statusDot: {
      width: "6px",
      height: "6px",
      backgroundColor: "#4ade80",
      borderRadius: "50%"
    }
  };

  return (
    <div style={styles.header}>
      <div style={styles.headerLeft}>
        <div style={styles.avatar}>L</div>
        <div style={styles.headerInfo}>
          <span style={styles.name}>Laura</span>
          <div style={styles.status}>
            <div style={styles.statusDot}></div>
            <span>Online</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Welcome component shown when no messages are present
const WelcomeMessage: React.FC = () => {
  const styles = {
    container: {
      display: "flex",
      flexDirection: "column" as const,
      alignItems: "center" as const,
      justifyContent: "center" as const,
      padding: "2rem 1rem",
      gap: "1.5rem",
      textAlign: "center" as const,
      backgroundColor: "rgba(26, 26, 26, 0.5)",
      borderRadius: "1rem",
      margin: "2rem 0",
      boxShadow: "0 8px 16px rgba(0, 0, 0, 0.1)",
      backdropFilter: "blur(8px)",
      border: "1px solid rgba(75, 85, 99, 0.15)"
    },
    avatarContainer: {
      position: "relative" as const,
      display: "inline-block",
      marginBottom: "1rem"
    },
    avatar: {
      width: "80px",
      height: "80px",
      borderRadius: "50%",
      backgroundColor: "#e95f43",
      display: "flex",
      alignItems: "center" as const,
      justifyContent: "center" as const,
      color: "#ffffff",
      fontSize: "32px",
      fontWeight: 600,
      boxShadow: "0 4px 20px rgba(233, 95, 67, 0.3)"
    },
    glowEffect: {
      position: "absolute" as const,
      top: "-5px",
      left: "-5px",
      right: "-5px",
      bottom: "-5px",
      borderRadius: "50%",
      background: "radial-gradient(circle, rgba(233, 95, 67, 0.2) 0%, rgba(233, 95, 67, 0) 70%)",
      zIndex: -1
    },
    title: {
      fontSize: "2rem",
      fontWeight: 700,
      color: "#ffffff",
      marginBottom: "0.5rem"
    },
    subtitle: {
      fontSize: "1.25rem",
      fontWeight: 400,
      color: "#cccccc",
      maxWidth: "650px",
      lineHeight: 1.5
    },
    suggestions: {
      display: "flex",
      flexDirection: "column" as const,
      gap: "0.5rem",
      width: "100%",
      maxWidth: "550px",
      marginTop: "1rem"
    },
    suggestionTitle: {
      fontSize: "14px",
      fontWeight: 600,
      color: "#cccccc",
      marginBottom: "0.5rem",
      textAlign: "left" as const
    },
    suggestionChips: {
      display: "flex",
      flexWrap: "wrap" as const,
      gap: "0.5rem",
      justifyContent: "flex-start" as const
    },
    chip: {
      backgroundColor: "rgba(42, 42, 42, 0.8)",
      borderRadius: "18px",
      padding: "8px 16px",
      fontSize: "14px",
      color: "#f0f0f0",
      border: "1px solid rgba(75, 85, 99, 0.3)",
      cursor: "pointer",
      transition: "all 0.2s ease",
      boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
      ":hover": {
        backgroundColor: "rgba(55, 55, 55, 0.9)",
        borderColor: "rgba(75, 85, 99, 0.5)"
      }
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.avatarContainer}>
        <div style={styles.avatar}>L</div>
        <div style={styles.glowEffect}></div>
      </div>
      <span style={styles.title}>Welcome!</span>
      <span style={styles.subtitle}>
        I’m Laura, Your iPhone Experience Survey Agent. Ready to have a conversation with you. <br></br>
        
        Click on the Voice Button below, to get started.
      </span>
      {/* <div style={styles.suggestions}>
        <span style={styles.suggestionTitle}>Try asking:</span>
        <div style={styles.suggestionChips}>
          <div style={styles.chip}>Help me with a problem</div>
          <div style={styles.chip}>Tell me about your capabilities</div>
          <div style={styles.chip}>Draft an email for me</div>
          <div style={styles.chip}>Analyze some data</div>
        </div>
      </div> */}
    </div>
  );
};

const Messages: React.FC = () => {
  const { messages } = useAgentMessage();
  // Create a ref for the messages container
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Function to scroll to bottom smoothly within the container
  const scrollToBottom = () => {
    const container = messagesContainerRef.current;
    if (container) {
      // Smoothly scroll the container to its bottom
      container.scrollTo({
        top: container.scrollHeight,
        behavior: "smooth"
      });
    }
  };

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]); // Dependency on messages array

  // Styles for Messages component
  const styles = {
    container: {
      display: "flex",
      flexDirection: "column" as const,
      justifyContent: "flex-start" as const,
      flexGrow: 1,
      minHeight: 0,
      backgroundColor: "rgba(18, 18, 18, 0.7)",
      borderRadius: "0.75rem",
      overflow: "hidden" as const,
      boxShadow: "0 12px 24px rgba(0, 0, 0, 0.2)"
    },
    chatContainer: {
      display: "flex",
      flexDirection: "column" as const,
      height: "100%",
      maxHeight: "85vh",
      overflow: "hidden" as const
    },
    messagesArea: {
      flexGrow: 1,
      overflowY: "auto" as const,
      padding: "1rem",
      backgroundColor: "rgba(20, 20, 20, 0.5)",
      display: "flex",
      flexDirection: "column" as const,
      gap: "0.25rem",
      position: "relative" as const,
      scrollBehavior: "smooth" as const,
      maxHeight: "calc(85vh - 200px)"
    },
    emptyState: {
      display: "flex",
      flexDirection: "column" as const,
      alignItems: "center" as const,
      justifyContent: "center" as const,
      height: "100%",
      padding: "2rem"
    },
    dateDivider: {
      display: "flex",
      alignItems: "center" as const,
      justifyContent: "center" as const,
      margin: "1rem 0",
      position: "relative" as const
    },
    dateLine: {
      position: "absolute" as const,
      top: "50%",
      left: 0,
      right: 0,
      height: "1px",
      backgroundColor: "rgba(75, 85, 99, 0.2)",
      zIndex: 0
    },
    dateText: {
      backgroundColor: "rgba(26, 26, 26, 0.95)",
      color: "#9ca3af",
      fontSize: "12px",
      padding: "4px 12px",
      borderRadius: "12px",
      position: "relative" as const,
      zIndex: 1,
      boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)"
    },
    typingIndicator: {
      display: "flex",
      alignItems: "center" as const,
      gap: "4px",
      padding: "8px 16px",
      backgroundColor: "rgba(42, 42, 42, 0.7)",
      borderRadius: "18px",
      maxWidth: "80px",
      margin: "8px 0 0 40px"
    },
    dot: {
      width: "8px",
      height: "8px",
      backgroundColor: "#f0f0f0",
      borderRadius: "50%",
      animation: "pulse 1.5s infinite ease-in-out",
      opacity: 0.7
    },
    dotDelayed1: {
      animationDelay: "0.2s",
      opacity: 0.5
    },
    dotDelayed2: {
      animationDelay: "0.4s",
      opacity: 0.3
    }
  };

  const today = new Date().toLocaleDateString('en-US', { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  return (
    <div style={styles.container}>
      <div style={styles.chatContainer}>
        <ChatHeader />
        
        <div style={styles.messagesArea} ref={messagesContainerRef}>
          {messages.length === 0 ? (
            <WelcomeMessage />
          ) : (
            <>
              <div style={styles.dateDivider}>
                <div style={styles.dateLine}></div>
                <span style={styles.dateText}>{today}</span>
              </div>
              
              {/* 
                Looking at the original code, the message order was:
                - Even indices (0, 2, 4...) for bot messages
                - Odd indices (1, 3, 5...) for user messages
                Preserving this exact logic:
              */}
              {messages.map((x: MessageData, index: number) => (
                <MessageBubble 
                  key={index}
                  messages={x.messages}
                  isBot={index % 2 != 0} // Even indices = bot, Odd indices = user
                />
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Messages;